import React from 'react';
import { Box, Paper, Text, Loader, Flex } from '@cowbell/ui';
import Moment from 'moment';
import type {
  EnrichedCmailMessage,
  MessageInfo,
  MessageInfoChild,
} from '../../../../api/cmail/cmail.dto';
import { CmailDetails } from './CmailDetails';
import { useGetCmailThread } from '../../CmailInbox/useGetCmailMessage';
import { transformMessage } from '../../../../api/cmail.api';
import { useCmailContext } from '../CmailStateProvider';
import classes from './CmailThreadedView.module.css';

interface CmailThreadedViewProps {
  message: MessageInfo | MessageInfoChild;
  enrichedMessage: EnrichedCmailMessage;
}

export const CmailThreadedView: React.FC<CmailThreadedViewProps> = ({
  message,
  enrichedMessage,
}) => {
  const cmailContext = useCmailContext();
  const filterType = cmailContext?.filterType || 'INBOX';

  const threadQuery = useGetCmailThread(message.msgId, 3, 20);

  const [expandedMessages, setExpandedMessages] = React.useState<Set<string>>(
    new Set()
  );

  const [showAllDepths] = React.useState(true);

  const threadData = React.useMemo(() => {
    if (!threadQuery.data) {
      return {
        messages: [message],
        failedIds: [],
        totalProcessed: 0,
        depthReached: 0,
      };
    }

    const { messages, failedIds, totalProcessed, depthReached } =
      threadQuery.data;

    const visibleMessages = showAllDepths
      ? messages
      : messages.filter((msg) => (msg as { depth: number }).depth <= 1);

    return {
      messages: visibleMessages,
      failedIds,
      totalProcessed,
      depthReached,
    };
  }, [threadQuery.data, message, showAllDepths]);

  React.useEffect(() => {
    if (threadData.messages.length > 1) {
      // For multi-message threads, expand all messages
      setExpandedMessages(
        new Set(
          threadData.messages.map(
            (m) => (m as MessageInfo | MessageInfoChild).msgId
          )
        )
      );
    } else {
      setExpandedMessages(new Set([message.msgId]));
    }
  }, [threadData.messages, message.msgId]);

  const toggleMessageExpansion = (msgId: string) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(msgId)) {
        newSet.delete(msgId);
      } else {
        newSet.add(msgId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString: string) => {
    return Moment(dateString).fromNow();
  };

  if (threadQuery.isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <Loader size="sm" />
        <Text size="sm" ml="sm">
          Loading email thread...
        </Text>
      </Box>
    );
  }

  if (threadQuery.isError) {
    return (
      <Box p="sm">
        <Text c="red">Failed to load email thread</Text>
      </Box>
    );
  }

  const { messages } = threadData;
  const showThreadControls = messages.length > 1;

  return (
    <Box>
      {messages.map((msg, index) => {
        const msgObj = msg as MessageInfo | MessageInfoChild;
        const isCurrentMessage = msgObj.msgId === message.msgId;
        const isExpanded = expandedMessages.has(msgObj.msgId);
        const enrichedMsg = isCurrentMessage
          ? enrichedMessage
          : transformMessage(
              msgObj as any,
              filterType?.toUpperCase() === 'SENT' ? 'SENT' : 'INBOX'
            );

        const threadContext = showThreadControls
          ? {
              isThreaded: true,
              isExpanded,
              onToggleExpansion: () => toggleMessageExpansion(msgObj.msgId),
              messageIndex: index,
              isCurrentMessage,
              totalMessages: messages.length,
              depth: (msgObj as { depth?: number }).depth || 0,
            }
          : null;

        return (
          <Paper
            key={msgObj.msgId}
            shadow="md"
            className={classes.detailsPaper}
            style={
              {
                '--thread-margin-bottom':
                  index === messages.length - 1 ? '0' : '1rem',
                '--thread-opacity': isCurrentMessage ? 1 : 0.9,
              } as React.CSSProperties
            }
          >
            <CmailDetails
              message={msgObj}
              enrichedMessage={enrichedMsg}
              threadContext={threadContext}
            />

            {showThreadControls && !isExpanded && (
              <Box className={classes.collapsedContent}>
                <Flex justify="space-between" align="flex-start">
                  <Box>
                    <Text size="sm" c="dimmed">
                      <strong>From:</strong>{' '}
                      {enrichedMsg.senderName || enrichedMsg.senderEmail}
                    </Text>
                    <Text size="sm" c="dimmed">
                      <strong>Subject:</strong> {msgObj.subject || 'No subject'}
                    </Text>
                  </Box>
                  <Flex
                    direction="column"
                    align="flex-end"
                    className={classes.dateColumn}
                  >
                    <Text size="sm" c="dimmed">
                      {formatDate(msgObj.dateReceived)}
                    </Text>
                    <Text size="sm" c="dimmed" fs="italic">
                      {getRelativeTime(msgObj.dateReceived)}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};
